import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal, EASE } from "./Reveal";
import { COLLECTION_CATEGORIES, COLLECTION_ITEMS, WHATSAPP_URL } from "@/lib/svenData";

// All imagery below is the client's real product photography — no AI-generated product images.
export const Collections = () => {
  const [active, setActive] = useState("all");
  const [lightbox, setLightbox] = useState(null);
  const items = COLLECTION_ITEMS.filter((it) => active === "all" || it.cat === active);
  const catLabel = (id) => COLLECTION_CATEGORIES.find((c) => c.id === id)?.label || "";

  const step = useCallback(
    (dir) => setLightbox((cur) => (cur === null ? null : (cur + dir + items.length) % items.length)),
    [items.length]
  );

  useEffect(() => {
    if (lightbox === null) {
      window.__lenis?.start();
      return;
    }
    window.__lenis?.stop();
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, step]);

  return (
    <section id="collections" data-testid="collections-gallery-section" className="bg-[#ffffff] py-20 lg:py-30">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
          <Reveal>
            <div>
              <p className="overline-label mb-5">The Wardrobe</p>
              <h2 className="font-display text-3xl sm:text-3xl lg:text-4xl font-medium text-[#141414]">
                Collections
              </h2>
              <p className="mt-6 text-sm font-light leading-relaxed text-[#57534e] max-w-xs">
                Build a wardrobe from quite everyday essentials to special occasion pieces.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="flex flex-wrap gap-2">
              {COLLECTION_CATEGORIES.map((c) => (
                <button
                  key={c.id}
                  data-testid={`collections-filter-${c.id}`}
                  onClick={() => setActive(c.id)}
                  className={`px-5 py-2 rounded-full font-mono-ov text-[10px] tracking-[0.15em] uppercase transition-all duration-300 border ${
                    active === c.id
                      ? "bg-[#000000] text-[#ffffff] border-[#0b0b0c]"
                      : "bg-transparent text-[#000000] border-[#000000] hover:border-[#c5a059] hover:text-[#c5a059]"
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </Reveal>
        </div>

        <motion.div layout className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-2 gap-0">
          <AnimatePresence mode="popLayout">
            {items.map((item, i) => (
              <motion.article
                key={item.src}
                layout
                data-testid={`collection-card-${item.cat}-${i}`}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.6, ease: EASE }}
                onClick={() => setLightbox(i)}
                className="group relative overflow-hidden bg-[#0b0b0c] cursor-zoom-in"
              >
                <div className="overflow-hidden aspect-[3/4]">
                  <img
                    src={item.src}
                    alt={item.name}
                    loading="lazy"
                    className="w-full h-full object-cover object-top transition-transform duration-[1.2s] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-110"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-70 group-hover:opacity-95 transition-opacity duration-500" />
                <div className="absolute bottom-0 left-0 right-0 p-6 translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
                  <p className="font-mono-ov text-[9px] tracking-[0.22em] uppercase text-[#c5a059] mb-2">{catLabel(item.cat)}</p>
                  <h3 className="font-display text-lg text-white">{item.name}</h3>
                  <a
                    href={`${WHATSAPP_URL}?text=${encodeURIComponent(`Hello Sven Suits, I'd like to enquire about the "${item.name}".`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`collection-enquire-${i}`}
                    className="inline-block mt-3 font-mono-ov text-[10px] tracking-[0.2em] uppercase text-white/0 group-hover:text-[#dfcca4] border-b border-transparent group-hover:border-[#c5a059]/60 transition-all duration-500 pb-0.5"
                  >
                    Enquire on WhatsApp →
                  </a>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <AnimatePresence>
        {lightbox !== null && items[lightbox] && (
          <motion.div
            data-testid="lookbook-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[100] bg-[#0b0b0c]/97 backdrop-blur-md flex flex-col"
            onClick={() => setLightbox(null)}
          >
            <div className="flex items-center justify-between px-5 sm:px-8 h-[72px] shrink-0">
              <p className="font-mono-ov text-[10px] tracking-[0.25em] uppercase text-white/50">
                {catLabel(items[lightbox].cat)} — {lightbox + 1} / {items.length}
              </p>
              <button
                data-testid="lightbox-close-btn"
                onClick={() => setLightbox(null)}
                aria-label="Close lookbook"
                className="flex items-center justify-center h-10 w-10 rounded-full border border-white/20 text-white hover:border-[#c5a059] hover:text-[#dfcca4] transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <div className="flex-1 flex items-center justify-center gap-3 sm:gap-8 px-3 sm:px-8 min-h-0">
              <button
                data-testid="lightbox-prev-btn"
                onClick={(e) => { e.stopPropagation(); step(-1); }}
                aria-label="Previous look"
                className="shrink-0 flex items-center justify-center h-11 w-11 sm:h-12 sm:w-12 rounded-full border border-white/20 text-white hover:border-[#c5a059] hover:text-[#dfcca4] hover:scale-105 transition-all"
              >
                <ChevronLeft size={20} />
              </button>

              <AnimatePresence mode="wait">
                <motion.img
                  key={items[lightbox].src}
                  data-testid="lightbox-image"
                  src={items[lightbox].src}
                  alt={items[lightbox].name}
                  initial={{ opacity: 0, scale: 0.97 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.35, ease: EASE }}
                  onClick={(e) => e.stopPropagation()}
                  className="max-h-full max-w-[calc(100%-120px)] sm:max-w-3xl object-contain select-none"
                />
              </AnimatePresence>

              <button
                data-testid="lightbox-next-btn"
                onClick={(e) => { e.stopPropagation(); step(1); }}
                aria-label="Next look"
                className="shrink-0 flex items-center justify-center h-11 w-11 sm:h-12 sm:w-12 rounded-full border border-white/20 text-white hover:border-[#c5a059] hover:text-[#dfcca4] hover:scale-105 transition-all"
              >
                <ChevronRight size={20} />
              </button>
            </div>

            <div className="shrink-0 px-5 sm:px-8 py-6 text-center" onClick={(e) => e.stopPropagation()}>
              <h3 data-testid="lightbox-title" className="font-display text-xl sm:text-2xl text-white">{items[lightbox].name}</h3>
              <a
                href={`${WHATSAPP_URL}?text=${encodeURIComponent(`Hello Sven Suits, I'd like to enquire about the "${items[lightbox].name}".`)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="lightbox-enquire-link"
                className="inline-block mt-2 font-mono-ov text-[10px] tracking-[0.2em] uppercase text-[#dfcca4] border-b border-[#c5a059]/60 pb-0.5 hover:text-white transition-colors"
              >
                Enquire on WhatsApp →
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
